import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconSearch, IconPlus, IconTools, IconEdit, IconTrash, IconUpload } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const ViewAllServices = () => {
    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();
    const [appId, setAppId] = useState(null);
    const [services, setServices] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarServicos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, page, debouncedSearchValue]);

    const listarServicos = async () => {
        const requestId = start();
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', page);
            if (debouncedSearchValue) params.set('search', debouncedSearchValue);
            const res = await api.get(`v1/apps/${appId}/services/?${params.toString()}`);
            if (!isCurrent(requestId)) return;
            setServices(res.data.services || []);
            setPagination({ count: res.data.count || 0, numPages: res.data.num_pages || 1 });
        } catch (error) {
            if (!isCurrent(requestId)) return;
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar serviços' });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value) => {
                setPage(1);
                setDebouncedSearchValue(value);
            }, 400),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSetSearch(value);
    };

    const handleDelete = async (service) => {
        if (!window.confirm(`Excluir o serviço "${service.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/services/${service.id}/`);
            listarServicos();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir serviço' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconTools}
                gradient="teal"
                title="Serviços"
                subtitle="Gerencie seu catálogo de serviços"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Buscar por nome, código..."
                            value={search}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/config/services/new')}
                        >
                            Novo Serviço
                        </Button>
                        <Button variant="outlined" startIcon={<IconUpload size={18} />} onClick={() => setImportDialogOpen(true)}>
                            Importar
                        </Button>
                    </Stack>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    {loading ? (
                        <LoadingBox label="Carregando serviços..." />
                    ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell>Unidade</TableCell>
                                <TableCell align="right">Preço</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {services.map((service) => (
                                <TableRow
                                    key={service.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`/dashboard/config/services/${service.id}/edit`)}
                                >
                                    <TableCell>{service.code || '—'}</TableCell>
                                    <TableCell>{service.name}</TableCell>
                                    <TableCell>{service.category_name || '—'}</TableCell>
                                    <TableCell>{service.unit_of_measure_code || '—'}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(service.price)}</TableCell>
                                    <TableCell>
                                        {service.is_active ? (
                                            <Chip label="Ativo" color="success" size="small" />
                                        ) : (
                                            <Chip label="Inativo" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" onClick={() => history.push(`/dashboard/config/services/${service.id}/edit`)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(service)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {services.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhum serviço cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    )}
                </CardContent>
            </GlassCard>

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Serviços"
                entityLabel="serviços"
                systemFields={[
                    { key: 'name', label: 'Nome do Serviço', required: true },
                    { key: 'category', label: 'Categoria', required: false },
                    { key: 'code', label: 'Código', required: false },
                    { key: 'price', label: 'Preço', required: true },
                    { key: 'unit_of_measure', label: 'Unidade de Medida (código)', required: false },
                    { key: 'tax_code', label: 'Código de Tributação (LC 116)', required: false },
                    { key: 'iss_rate', label: 'Alíquota de ISS (%)', required: false }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/service_fields/`}
                importUrl={`v1/apps/${appId}/services/import/`}
                onImported={() => listarServicos()}
            />
        </Box>
    );
};

function debounce(func, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

export default ViewAllServices;
